import React, { useEffect, useState } from "react";
import api, { formatDateTime, formatApiError } from "../lib/api";
import { History, RotateCcw } from "lucide-react";

/**
 * Inline expandable panel showing every visit that drew from a package,
 * with a "Reverse last usage" button (auto-disabled outside the 24h window).
 * Used by both /admin/packages (ledger) and /admin/clients/:id (per-client tab).
 */
export default function PackageUsageDrill({ packageId, onReverse, onError }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/packages/${packageId}/usages`).then(({ data }) => { if (!cancelled) setData(data); });
    return () => { cancelled = true; };
  }, [packageId]);

  const reverseLast = async () => {
    setBusy(true);
    try {
      await api.post(`/packages/${packageId}/reverse-usage`);
      const { data: refreshed } = await api.get(`/packages/${packageId}/usages`);
      setData(refreshed);
      if (onReverse) await onReverse();
    } catch (e) {
      if (onError) onError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="px-6 py-4 bg-cream/30 text-xs font-ui text-slate">Loading usage history…</div>;

  const activeUsages = (data.usages || []).filter((u) => !u.reversed_at);
  const canReverse = activeUsages.length > 0 && (() => {
    const last = activeUsages[0];
    if (!last?.deducted_at) return false;
    return (Date.now() - new Date(last.deducted_at).getTime()) <= 86400 * 1000;
  })();

  const remaining = data.package
    ? data.package.original_quantity - data.package.used_quantity
    : null;

  return (
    <div className="px-6 py-4 bg-cream/30 border-t border-hairline" data-testid={`package-usage-drill-${packageId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-ui text-charcoal">
          <History className="w-4 h-4 text-sage" />
          Usage history ({data.usages.length})
          {remaining != null && (
            <span className="ml-2 font-mono text-xs text-slate">
              {remaining} of {data.package.original_quantity} sessions remaining
            </span>
          )}
        </div>
        <button
          disabled={!canReverse || busy}
          onClick={reverseLast}
          className="px-3 py-1.5 text-xs font-ui border border-forest text-forest rounded-md hover:bg-cream disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          data-testid={`reverse-usage-${packageId}`}
          title={canReverse ? "Reverse the most recent usage (within 24h)" : "No reversible usage in the last 24h"}
        >
          <RotateCcw className="w-3 h-3" /> Reverse last usage
        </button>
      </div>
      {data.usages.length === 0 ? (
        <p className="text-xs font-ui text-slate">No sessions drawn yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {data.usages.map((u) => (
            <li key={u.id} className="text-xs font-ui flex items-center justify-between gap-3">
              <span className={u.reversed_at ? "line-through text-slate" : "text-charcoal"}>
                {u.appointment?.service?.name || "Session"}
                {u.appointment?.practitioner?.display_name && <span className="text-slate"> · {u.appointment.practitioner.display_name}</span>}
              </span>
              <span className="text-slate font-mono">
                {formatDateTime(u.deducted_at)}
                {u.reversed_at && <span className="ml-1 text-status-danger">(reversed)</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
